import React, { useEffect, useMemo, useState } from "react";
import styles from "./Profile.module.scss";
import Header from "./components/Header";
import Overview from "./tabs/Overview"
import Followers from "./tabs/Followers";
import Achievements from "./tabs/Achievements/Achievements";
import Games from "./tabs/Games";
import Post from "./tabs/Post";
import { HippocampusAPI } from "../../services/api/HcgApi";
import { api } from "../../services/api";
import { useLocation, useSearchParams } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import TabBar from "../../components/TabBar/TabBar";
import { SkeletonContainer } from "../../components/Skeleton";
import ProfileSkeleton from "./components/ProfileSkeleton";

const PROFILE_TABS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'post', label: 'Post' },
  { id: 'games', label: 'Games' },
  { id: 'achievements', label: 'Achievements' },
] as const;

const Profile: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [params] = useSearchParams();
  const userId = params.get("user");
  const { user } = useUser();
  const [userProfile, setUserProfile] = useState<any>({})
  const hcgApi = useMemo(() => new HippocampusAPI(), []);
  const [loading, setLoading] = useState<boolean>(true)
  const [followerCount, setFollowerCount] = useState<number>(0);
  const [followingCount, setFollowingCount] = useState<number>(0);
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true)
      try {
        const profile = await hcgApi.getProfile(userId || "");

        profile.isMe =
          !!user &&
          !!user.userId &&
          (!!userId ? user.userId === userId : user.userId === profile.userId);

        setUserProfile(profile);

        // For restricted private profiles, use counts from the API response directly
        if (profile.isPrivate && !profile.linkedAccounts) {
          setFollowerCount(profile.followerCount ?? 0);
          setFollowingCount(profile.followingCount ?? 0);
        } else {
          // Fetch follower and following counts for full profiles
          const profileUserId = userId || profile.userId;
          if (profileUserId) {
            try {
              const [followersData, followingData] = await Promise.all([
                api.social.getFollowers(profileUserId, 1),
                api.social.getFollowing(profileUserId, 1),
              ]);
              setFollowerCount(followersData.total);
              setFollowingCount(followingData.total);
            } catch (error) {
              console.error("Failed to fetch follower/following counts:", error);
            }
          }
        }

      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false)
      }
    };
    
    // only fetch if we have enough info
    if (userId || user?.userId) {
      fetchUser();
    }
  }, [location]);


  return (
    <div className={styles.profilePage}>
      <SkeletonContainer
        loading={loading}
        skeleton={<ProfileSkeleton />}
        label="profile"
      >
        <Header 
          profile={userProfile} 
          onFollowChange={(isFollowing) => {
            // Update follower count when follow state changes
            setFollowerCount(prev => isFollowing ? prev + 1 : Math.max(0, prev - 1));
          }}
        />
        {/* Main Content */}
        <div>
          <div className={styles.follow}>
            <button
              className={`${styles.tab} ${activeTab === "followers" ? styles.active : ""}`}
              onClick={() => setActiveTab("followers")}
            >
              Followers {followerCount}
            </button>
            <span className={styles.tab}>|</span>
            <button
              className={`${styles.tab} ${activeTab === "following" ? styles.active : ""}`}
              onClick={() => setActiveTab("following")}
            >
              Following {followingCount}
            </button>
          </div>
        </div>
        {userProfile.isPrivate && !userProfile.isMe && !userProfile.linkedAccounts ? (
          <div className={styles.privateMessage} data-testid="private-account-message">
            <p>This account is private</p>
            <p>Follow this account to see their posts and activity</p>
          </div>
        ) : (
          <>
            <div className={styles.contentWrapper}>
              <TabBar tabs={PROFILE_TABS} activeTab={activeTab} onChange={setActiveTab} />
            </div>
            <div className={styles.tabContent}>
              {activeTab === "overview" && <Overview profile={userProfile} />}
              {activeTab === "post" && <Post userId={userProfile?.userId || userId || ""} isOwnProfile={!!userProfile?.isMe} />}
              {activeTab === "followers" && <Followers mode="followers" userId={userId || userProfile?.user?.userId} />}
              {activeTab === "following" && <Followers mode="following" userId={userId || userProfile?.user?.userId} />}
              {activeTab === "achievements" && <Achievements profile={userProfile} />}
              {activeTab === "games" && <Games profile={userProfile}/>}
            </div>
          </>
        )}
      </SkeletonContainer>
    </div>
  );
};

export default Profile;
